import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { PlatformStatusBanner } from "@/components/PlatformStatusBanner";
import { CommandPalette } from "@/components/CommandPalette";
import { ErrorGuard } from "@/system/errorGuard";
import { useSocketConnection } from "@/hooks/useSocketConnection";
import { useObservabilitySocket } from "@/hooks/useObservabilitySocket";
import { initIncidentRealtime } from "@/services/platformStatusService";
import { initWorkflowService } from "@/services/workflowOrchestrationService";

/**
 * WorkspaceLayout - used exclusively for the project workspace route.
 *
 * The workspace is a full-viewport, single-page "canvas" experience:
 * conversation stream + sticky composer + live preview. It cannot share
 * the standard Layout's overflow-auto + padded main because:
 *
 *   1. SidebarProvider / SidebarContent both use min-h-svh (not h-dvh).
 *      On real mobile, when the address bar retracts (dvh > svh), the
 *      workspace height (100dvh - TopBar) exceeds what main provides,
 *      causing main to scroll and breaking the sticky-composer contract.
 *
 *   2. Layout's main has p-4/6/8 padding + overflow-auto. Negative-margin
 *      hacks to escape this padding are fragile across breakpoints and
 *      don't survive the svh/dvh mismatch.
 *
 * This layout pins the outer container to exactly 100dvh with overflow-hidden,
 * removes padding from main, and lets main be a proper flex-1 min-h-0
 * container so that the workspace's h-full fills it correctly.
 */
export function WorkspaceLayout() {
  useSocketConnection();
  useObservabilitySocket();

  useEffect(() => initIncidentRealtime(), []);
  useEffect(() => initWorkflowService(), []);

  return (
    // MANDATE 32 sections 24/25: the workspace has its OWN full navigation (WorkspaceNav), so the
    // global AppSidebar defaults to a compact icon rail here instead of a second full-width sidebar.
    // That reclaims ~200px and is what fixes the multi-column crush (global sidebar + workspace nav +
    // chat + preview) the founder saw at constrained widths. AppSidebar is collapsible="icon", so it
    // stays one tap/hover away; on mobile the provider renders it off-canvas (no width cost).
    <SidebarProvider defaultOpen={false}>
      <div
        className="h-dvh overflow-hidden flex w-full"
        style={{
          background: "radial-gradient(ellipse 120% 60% at 70% 0%, hsl(250 30% 8% / 0.8) 0%, hsl(228 14% 5%) 55%)",
          backgroundColor: "hsl(var(--background))",
        }}
      >
        <AppSidebar />

        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <PlatformStatusBanner />
          <a href="#main-content" className="nv-skip-link">Skip to main content</a>
          {/* No global TopBar here: the workspace renders ONE command bar of its
              own (identity, preview, tools, notifications, credits). Stacking the
              global bar above it duplicated the brand and the project switcher and
              cost 56px that belongs to the stream. The global bar remains on every
              standard Layout route; the icon rail (AppSidebar) still carries the
              brand and global navigation here. */}

          {/* No padding, no overflow-auto - workspace manages its own scroll */}
          <main id="main-content" tabIndex={-1} className="flex-1 min-h-0 overflow-hidden">
            <ErrorGuard name="route">
              <Outlet />
            </ErrorGuard>
          </main>
        </div>
      </div>

      {/* The credit-exhausted state is no longer a floating top toast (it overlapped the header and was
          cut off on real devices). It now renders inline above the composer as ComposerCreditNotice,
          at the point where the user actually acts. */}
      <CommandPalette />
    </SidebarProvider>
  );
}
